import React, { useMemo } from 'react';
import { defaultAssetInstructions } from 'fym-shared/src/music24/assetInstructions';
import type { KitTextLocale } from 'fym-shared/src/music24/kitText';
import type { ReleaseKitLook } from 'fym-shared/src/music24/releaseThemes';
import { reelCredits } from 'fym-shared/src/music24/music24Plans';
import { TypedMemo } from '../../../../common/types';
import { Panel } from '../../../components/primitives/Panel';
import { useMusic24Capabilities } from '../../../hooks/useMusic24Capabilities';
import { ReelDraft } from '../aiReel/useReelDraft';
import { ReelLyricsField } from '../aiReel/ReelLyricsField';
import { ReelRail } from '../aiReel/ReelRail';
import { releaseArtworkUrl, windowPreviewArtworkPx } from '../aiReel/releaseArtworkUrl';
import { AiReelPreview } from '../frames/AiReelPreview';
import { InstructionsField } from './InstructionsField';
import { StudioActionFeedback } from '../studioActionErrorText';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import styles from '../Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The width of the first-frame preview in the rail (the 9:16 stand-in is at most 200 px wide). */
const PREVIEW_WIDTH = 200;

interface AiReelDetailsProps {
  accountId: number;
  draft: ReelDraft;
  /** The look of the song: the reel follows its theme. */
  look: ReleaseKitLook;
  /** The cover palette of the song: the preview takes the colours of its theme. */
  palette: readonly string[];
  /** The language of the kit words of the song (`useSongTheme().storyLocale`); absent: English. */
  locale?: KitTextLocale;
  isGenerating: boolean;
  /** True after a 503 `AI_REELS_UNAVAILABLE`. */
  isUnavailable: boolean;
  onGenerate: () => void;
  /** The answer of a failed Generate: the rail shows it under the button. */
  actionFeedback?: StudioActionFeedback | null;
}

/**
 * Step 3 for the AI reel (design n4, q3e; z6 owner answers 2026-10-08): the reel form (Instructions)
 * inside the Studio. The reel is one length (15 s) on the audio hook of the song, with the synced
 * lyric lines, the hook card, the release chip, the cover on the drop and the end card, so the form
 * has no waveform, voice or length picker. The rail shows the first frame as the render draws it
 * (`AiReelPreview`). Generate calls `createReel` as before; the reel then opens its review page.
 */
const AiReelDetailsComponent: React.FC<AiReelDetailsProps> = ({
  accountId,
  draft,
  look,
  palette,
  locale,
  isGenerating,
  isUnavailable,
  onGenerate,
  actionFeedback,
}) => {
  const release = draft.item;
  const { clipWatermark } = useMusic24Capabilities();
  const defaultInstructions = useMemo(
    () => defaultAssetInstructions({ assetTypeId: 'aiReel', releaseThemeId: look.themeId, waveform: 'none' }),
    [look.themeId]
  );
  const preview =
    release === undefined ? undefined : (
      <AiReelPreview
        look={look}
        palette={palette}
        title={release.title}
        artist={release.artistName}
        releaseDate={release.releaseDate?.slice(0, 10) ?? null}
        artworkUrl={releaseArtworkUrl(release, windowPreviewArtworkPx())}
        watermark={clipWatermark}
        width={PREVIEW_WIDTH}
        locale={locale}
      />
    );

  return (
    <div className={styles.details}>
      <Panel>
        {/* The lyrics of the row win; the box shows only for a row with no lyrics. */}
        {release?.song.hasLyrics !== true && <ReelLyricsField value={draft.lyrics} onChange={draft.setLyrics} />}
        <InstructionsField
          value={draft.instructions ?? defaultInstructions}
          defaultValue={defaultInstructions}
          onChange={draft.setInstructions}
          onReset={draft.resetInstructions}
        />
      </Panel>
      <ReelRail
        accountId={accountId}
        releaseTitle={release?.title}
        themeId={look.themeId}
        preview={preview}
        cost={reelCredits()}
        canGenerate={draft.createOptions !== undefined && release !== undefined}
        isGenerating={isGenerating}
        isUnavailable={isUnavailable}
        onGenerate={onGenerate}
        actionFeedback={actionFeedback}
      />
    </div>
  );
};

export const AiReelDetails = TypedMemo(AiReelDetailsComponent);
